
import { CheckCircle, Download, Info, Pencil, TriangleAlert, Upload, Wrench } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/Common/Card';
import { Button } from '@/components/Common/button';
import { ConfigFile } from './types';

interface ConfigurationGroupTabProps {
  files: ConfigFile[];
  expandedSpecs: Record<string, boolean>;
  recentlySaved: Record<string, boolean>;
  onToggleSpecs: (fileId: string) => void;
  onViewFile: (fileId: string) => void;
  onDownloadFile: (fileId: string) => void;
  onUploadClick: (fileId: string) => void;
  isStructuredEditor: (fileId: string) => boolean;
}

export function ConfigurationGroupTab({
  files,
  expandedSpecs,
  recentlySaved,
  onToggleSpecs,
  onViewFile,
  onDownloadFile,
  onUploadClick,
  isStructuredEditor,
}: ConfigurationGroupTabProps) {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {files.map((configFile) => (
        <Card key={configFile.id} className="border-border">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <CardTitle className="text-lg font-semibold text-foreground flex items-center gap-2">
                  {t(configFile.titleKey)}
                  {configFile.uploaded && !configFile.error && (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  )}
                  {configFile.error && <TriangleAlert className="h-5 w-5 text-amber-500" />}
                </CardTitle>
                <CardDescription className="text-sm text-muted-foreground mt-1">
                  <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{configFile.filename}</code>
                  {' • '}
                  <span className="font-medium">{configFile.format}</span>
                </CardDescription>
              </div>
              <button
                onClick={() => onToggleSpecs(configFile.id)}
                className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-muted-foreground hover:text-foreground bg-muted/50 hover:bg-muted rounded-md transition-colors"
              >
                <Info className="h-3.5 w-3.5" />
                {t('knowledgeManagement.configCard.specsButton')}
              </button>
            </div>

            {configFile.error && (
              <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mt-2">
                <div className="flex items-start gap-2">
                  <TriangleAlert className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-amber-900 dark:text-amber-100 mb-1">
                      {t('knowledgeManagement.configCard.invalidFile')}
                    </p>
                    <p className="text-xs text-amber-800 dark:text-amber-200">{configFile.error}</p>
                  </div>
                </div>
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-foreground/80">{t(configFile.descriptionKey)}</p>

            {expandedSpecs[configFile.id] && (
              <div className="bg-muted/50 rounded-lg p-4 border border-border">
                <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                  <Info className="h-4 w-4" />
                  {t('knowledgeManagement.configCard.specsTitle')}
                </h4>
                <ul className="text-sm text-foreground/70 space-y-1">
                  {configFile.specKeys.map((specKey) => (
                    <li key={specKey} className="flex items-start gap-2">
                      <span className="text-primary mt-0.5">•</span>
                      <span>{t(specKey)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {recentlySaved[configFile.id] && (
              <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-950/20 px-3 py-2 rounded-lg border border-green-200 dark:border-green-800">
                <CheckCircle className="h-4 w-4 flex-shrink-0" />
                <span>{t('knowledgeManagement.configCard.savedSuccess')}</span>
              </div>
            )}

            <div className="flex justify-end items-center gap-2 pt-2">
              {configFile.uploaded && (
                <Button onClick={() => onDownloadFile(configFile.id)} variant="outline" size="sm">
                  <Download className="h-4 w-4 mr-2" />
                  {t('knowledgeManagement.configCard.download')}
                </Button>
              )}
              {configFile.uploaded && (
                <Button
                  onClick={() => onViewFile(configFile.id)}
                  variant="outline"
                  className="bg-primary/10 border-primary/30 text-primary hover:bg-primary/20"
                  size="sm"
                >
                  {isStructuredEditor(configFile.id) ? (
                    <Wrench className="h-4 w-4 mr-2" />
                  ) : (
                    <Pencil className="h-4 w-4 mr-2" />
                  )}
                  {isStructuredEditor(configFile.id) ? t('actions.edit') : t('global.viewModify')}
                </Button>
              )}
              <Button onClick={() => onUploadClick(configFile.id)} variant="outline" size="sm">
                <Upload className="h-4 w-4 mr-2" />
                {t('knowledgeManagement.configCard.upload')}
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
